import React, { useState, useEffect } from "react";
import { _, _any } from "i18n";
import Modal from "ui-kit/src/Modal/Modal";
import { Button } from "../../../../../ui-kit-v2/src/button/button";

import "./css/AddLinkModal.css";
import { GameAnalysisPhysicalStatus } from "./constants";
import { GameAnalysisPhysicalResponse } from "../../../../../portal/src/libs/apiClient";
import clsx from "clsx";
import { ChartSpline, Loader2 } from "lucide-react";
import { randomUUID } from "../../functions";
import { parseMessageAttribute } from "../../../../../admin/src/libs/utils";

interface AddLinkModalProps {
	physicalAnalysisReportData?: GameAnalysisPhysicalResponse;
	handlePostPhysicalAnalysisReportUrls?: (urls: string[]) => Promise<void>;
	toastMessage: (type: string, message: string) => void;
}

type NextVideoLink = {
	id: string;
	value: string;
	isValid: boolean;
	isDuplicate: boolean;
};

export function AddLinkModal({
	physicalAnalysisReportData,
	handlePostPhysicalAnalysisReportUrls,
	toastMessage,
}: AddLinkModalProps) {
	const [disabledSave, setDisabledSave] = useState<boolean>(true);
	const [showAddLinkModal, setShowAddLinkModal] = useState<boolean>(false);

	const defaultVideoLink = {
		id: randomUUID(),
		value: "",
		isValid: true,
		isDuplicate: false,
	};
	const [nextVideoLinks, setNextVideoLinks] = useState<NextVideoLink[]>([
		defaultVideoLink,
	]);

	const MAX_LINKS = 5;

	useEffect(() => {
		const hasInvalid = nextVideoLinks.some(
			(link: NextVideoLink) => !link.isValid,
		);
		const allEmpty = nextVideoLinks.every(
			(link: NextVideoLink) => link.value.trim() === "",
		);
		setDisabledSave(hasInvalid || allEmpty);
	}, [nextVideoLinks]);

	const handleAddLink = () => {
		if (nextVideoLinks.length < MAX_LINKS) {
			setNextVideoLinks([
				...nextVideoLinks,
				{ ...defaultVideoLink, id: randomUUID() },
			]);
		}
	};

	const handleRemoveLink = (index: number) => {
		setNextVideoLinks(
			nextVideoLinks.filter(
				(link: NextVideoLink, i: number) =>
					link.id !== nextVideoLinks[index].id,
			),
		);
	};

	const handleChange = (
		linkId: string,
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const input = e.target;
		const value = input.value.trim();

		setNextVideoLinks((prev: NextVideoLink[]) => {
			const index = prev.findIndex((link) => link.id === linkId);
			if (index === -1) {
				return prev;
			}

			const isDuplicate =
				value !== "" &&
				prev.some((link) => link.id !== linkId && link.value.trim() === value);

			const updated = [...prev];

			updated[index] = {
				...updated[index],
				value,
				isValid: (value === "" || !isDuplicate) && input.checkValidity(),
				isDuplicate,
			};

			return updated;
		});
	};

	const [savingVideoUrls, setSavingVideoUrls] = useState<boolean>(false);

	const handleSave = async () => {
		setSavingVideoUrls(true);
		const urls = nextVideoLinks.reduce<string[]>((acc: string[], link: NextVideoLink) => {
			const trimmed = link.value.trim();
			if (trimmed && !acc.includes(trimmed)) acc.push(trimmed);
			return acc;
		}, [] as string[]);

		if (!urls.length) {
			setSavingVideoUrls(false);
			setShowAddLinkModal(false);
			setDisableAnalysisBtn(true);
			return;
		}

		try {
			await handlePostPhysicalAnalysisReportUrls?.(urls);
			toastMessage("success", _("gamePage.physicalMetrics.modal.savedVideoUrl"));
		} catch (err: unknown) {
			toastMessage(
				"error",
				parseMessageAttribute(err) || _("gamePage.physicalMetrics.unknownErrorOccured"),
			);
		} finally {
			setSavingVideoUrls(false);
			setShowAddLinkModal(false);
			setDisableAnalysisBtn(true);
		}
	};

	const [disableAnalysisBtn, setDisableAnalysisBtn] = useState<boolean>(true);
	useEffect(() => {
		if (
			[
				GameAnalysisPhysicalStatus.REQUESTED,
				GameAnalysisPhysicalStatus.IN_PROGRESS,
				GameAnalysisPhysicalStatus.REVIEW,
				GameAnalysisPhysicalStatus.DONE,
			].includes(
				physicalAnalysisReportData?.analysisStatus as GameAnalysisPhysicalStatus,
			)
		) {
			setDisableAnalysisBtn(true);
		} else {
			setDisableAnalysisBtn(false);
		}
	}, [physicalAnalysisReportData]);

	return (
		<div className="physical-analysis-container">
			<div className="adminOptionsItem-label">
				{_("gamePage.label.physicalMetrics")}:
				{physicalAnalysisReportData?.analysisStatus && (
					<>
						&nbsp;
						<span>
							{_any(
								`gamePage.physicalMetrics.status.${physicalAnalysisReportData?.analysisStatus}`,
							)}
						</span>
					</>
				)}
			</div>
			<Button
				type="button"
				className={clsx("adminOptionsItem-button with-icon w-max", {
					disabled: disableAnalysisBtn,
				})}
				onClick={() => {
					setShowAddLinkModal(true);
				}}
				disabled={disableAnalysisBtn}
				iconLeft={<ChartSpline size={16} />}
			>
				<span>{_("gamePage.physicalMetrics.request")}</span>
			</Button>
			{physicalAnalysisReportData?.rejectedReason?.trim() && (
				<p className="physical-analysis-rejected-reason">
					<span className="physical-analysis-rejected-reason__label">
						{_("gamePage.physicalMetrics.rejectedReason")}:
					</span>{" "}
					{_any(physicalAnalysisReportData.rejectedReason.trim())}
				</p>
			)}
			<Modal
				show={showAddLinkModal}
				size="md"
				onClose={() => {
					setShowAddLinkModal(false);
				}}
			>
				<div className="add-link-modal-container">
					<div className="add-link-modal-header">
						<h2>{_("gamePage.physicalMetrics.statsLink")}</h2>
					</div>
					<div className="add-link-modal-body">
						{nextVideoLinks.map((link, index) => (
							<div className="add-link-body__row-item" key={link.id}>
								<h3 className="field-title">
									{index === 0
										? _("gamePage.physicalMetrics.downloadLink")
										: `${_("gamePage.physicalMetrics.nextVideoLink")} ${index + 1}`}
								</h3>
								<div className="field-content">
									<input
										type="url"
										className={`itemInput ${!link.isValid ? "invalid" : ""}`}
										value={link.value}
										onChange={(e) => handleChange(link.id, e)}
										placeholder="https://example.com/video"
									/>
									{index === 0 ? (
										<Button
											onClick={handleAddLink}
											disabled={nextVideoLinks.length === MAX_LINKS}
											className={clsx("btn btn--grey w-max", {
												disabled: nextVideoLinks.length === MAX_LINKS,
											})}
										>
											+ {_("gamePage.physicalMetrics.addNextLink")}
										</Button>
									) : (
										<Button
											className="btn btn--grey w-max remove-link-button"
											onClick={() => handleRemoveLink(index)}
										>
											<span className="icon-trash-new icon-new icon-grey icon-16" />
										</Button>
									)}
								</div>
								{!link.isValid && !link.isDuplicate && (
									<p className="error-text-input">
										{_("common.validation.url")}
									</p>
								)}
								{link.isDuplicate && (
									<p className="error-text-input">
										{_("gamePage.physicalMetrics.modal.duplicateUrl")}
									</p>
								)}
							</div>
						))}
					</div>
					<div className="add-link-modal-footer">
						<Button
							className="btn btn--cancel w-max"
							variant="secondary"
							onClick={() => {
								setShowAddLinkModal(false);
							}}
						>
							{_("common.action.cancel")}
						</Button>
						<Button
							className={clsx("btn btn--grey w-max", {
								disabled: disabledSave || savingVideoUrls,
							})}
							disabled={disabledSave || savingVideoUrls}
							onClick={handleSave}
							iconLeft={
								savingVideoUrls ? (
									<Loader2
										className="animate-spin inline-block ml-1 text-gray-400"
										size={14}
									/>
								) : undefined
							}
						>
							{_("common.action.save")}
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
}